// What the person picked, and what is arithmetically true about it.
//
// Nothing here removes a pick, reorders one, or calls a plan impossible. Two
// sets that overlap are two sets that overlap: the person can see that, and
// they may well intend it. This reports the numbers and stops.
//
// Two facts are worth putting next to a pair of picks. They share minutes, or
// they share none but the gap between them is shorter than the walk. Both are
// information. Neither is a verdict.

import type { FestivalSet, Id, Pick, WalkMatrix } from './types.ts';
import { walkMinutes } from './walk.ts';
import { overlapMs } from './time.ts';

const MINUTE = 60000;

export interface ResolvedPick {
  setId: Id;
  set: FestivalSet;
  /** Where the person plans to arrive, which is the set start unless they said otherwise. */
  startTs: number;
  endTs: number;
  arrivesLate: boolean;
  leavesEarly: boolean;
}

export type ClashKind = 'overlap' | 'tight_walk';

export interface Clash {
  id: string;
  kind: ClashKind;
  first: ResolvedPick;
  second: ResolvedPick;
  /** Minutes the two spans share. Zero for a tight walk. */
  overlapMinutes: number;
  /** Minutes between the end of the first and the start of the second. */
  gapMinutes: number;
  walkMinutes: number;
  /** Walk minutes the gap does not cover. Positive means they are short. */
  shortByMinutes: number;
}

/** Resolves picks against the catalog, applying the person's own arrive and leave times. */
export function resolvePicks(picks: Pick[], sets: Map<Id, FestivalSet>): ResolvedPick[] {
  const out: ResolvedPick[] = [];
  for (const p of picks) {
    const set = sets.get(p.setId);
    if (!set) continue;
    const startTs = clampToSet(p.startTs ?? set.startTs, set);
    const endTs = clampToSet(p.endTs ?? set.endTs, set);
    if (endTs <= startTs) continue;
    out.push({
      setId: set.id,
      set,
      startTs,
      endTs,
      arrivesLate: startTs > set.startTs,
      leavesEarly: endTs < set.endTs,
    });
  }
  return out.sort((a, b) => a.startTs - b.startTs || a.endTs - b.endTs);
}

function clampToSet(ts: number, set: FestivalSet): number {
  return Math.max(set.startTs, Math.min(set.endTs, ts));
}

// Pairs worth a note: those that share minutes, and those whose gap is shorter
// than the walk. A pair with something else entirely between them is not a
// decision anybody is making, so it is skipped.
export function findClashes(resolved: ResolvedPick[], walk: WalkMatrix): Clash[] {
  const out: Clash[] = [];

  for (let i = 0; i < resolved.length; i++) {
    for (let j = i + 1; j < resolved.length; j++) {
      const a = resolved[i];
      const b = resolved[j];

      const shared = overlapMs(a.startTs, a.endTs, b.startTs, b.endTs) / MINUTE;
      const gap = (b.startTs - a.endTs) / MINUTE;
      const walkMins =
        a.set.stageId === b.set.stageId ? 0 : walkMinutes(walk, a.set.stageId, b.set.stageId);

      if (shared > 0) {
        out.push({
          id: `clash:${a.setId}:${b.setId}`,
          kind: 'overlap',
          first: a,
          second: b,
          overlapMinutes: Math.round(shared),
          gapMinutes: Math.round(gap),
          walkMinutes: walkMins,
          shortByMinutes: Math.round(walkMins - gap),
        });
        continue;
      }

      if (gap >= 0 && gap < walkMins) {
        out.push({
          id: `clash:${a.setId}:${b.setId}`,
          kind: 'tight_walk',
          first: a,
          second: b,
          overlapMinutes: 0,
          gapMinutes: Math.round(gap),
          walkMinutes: walkMins,
          shortByMinutes: Math.round(walkMins - gap),
        });
      }
    }
  }

  return out;
}

export interface ClashOption {
  id: string;
  label: string;
  /** Spans this option would write onto the picks, if the person chooses it. */
  spans: { setId: Id; startTs: number; endTs: number }[];
  /** Sets this option would drop, which happens only when the person chooses it. */
  drops: Id[];
  minutesKept: { setId: Id; minutes: number }[];
}

// Ways through one clash, as choices. Nothing is applied: the caller renders
// these, the person takes one or ignores all of them, and the picks change only
// if they say so. Keeping both as they are is not listed, because it is what
// already stands.
export function optionsForClash(
  clash: Clash,
  nameOf: (setId: Id) => string,
  formatTime: (ts: number) => string,
): ClashOption[] {
  const { first, second } = clash;
  const walkMs = clash.walkMinutes * MINUTE;
  const options: ClashOption[] = [];

  const firstName = nameOf(first.setId);
  const secondName = nameOf(second.setId);
  const keep = (setId: Id, startTs: number, endTs: number) => ({
    setId,
    minutes: Math.round((endTs - startTs) / MINUTE),
  });

  const leaveBy = second.set.startTs - walkMs;
  if (leaveBy > first.set.startTs) {
    const cut = Math.min(leaveBy, first.set.endTs);
    options.push({
      id: `${clash.id}:leave-first-early`,
      label: `Leave ${firstName} at ${formatTime(cut)}, catch all of ${secondName}`,
      spans: [
        { setId: first.setId, startTs: first.set.startTs, endTs: cut },
        { setId: second.setId, startTs: second.set.startTs, endTs: second.set.endTs },
      ],
      drops: [],
      minutesKept: [
        keep(first.setId, first.set.startTs, cut),
        keep(second.setId, second.set.startTs, second.set.endTs),
      ],
    });
  }

  const arriveAt = first.set.endTs + walkMs;
  if (arriveAt < second.set.endTs) {
    const start = Math.max(arriveAt, second.set.startTs);
    options.push({
      id: `${clash.id}:join-second-late`,
      label: `All of ${firstName}, join ${secondName} at ${formatTime(start)}`,
      spans: [
        { setId: first.setId, startTs: first.set.startTs, endTs: first.set.endTs },
        { setId: second.setId, startTs: start, endTs: second.set.endTs },
      ],
      drops: [],
      minutesKept: [
        keep(first.setId, first.set.startTs, first.set.endTs),
        keep(second.setId, start, second.set.endTs),
      ],
    });
  }

  const window = second.set.endTs - first.set.startTs - walkMs;
  if (window > 0) {
    const cut = first.set.startTs + Math.round(window / 2);
    const start = Math.max(cut + walkMs, second.set.startTs);
    if (cut > first.set.startTs && cut <= first.set.endTs && start < second.set.endTs) {
      options.push({
        id: `${clash.id}:halves`,
        label: `Half each: out of ${firstName} at ${formatTime(cut)}, into ${secondName} at ${formatTime(start)}`,
        spans: [
          { setId: first.setId, startTs: first.set.startTs, endTs: cut },
          { setId: second.setId, startTs: start, endTs: second.set.endTs },
        ],
        drops: [],
        minutesKept: [
          keep(first.setId, first.set.startTs, cut),
          keep(second.setId, start, second.set.endTs),
        ],
      });
    }
  }

  options.push({
    id: `${clash.id}:drop-second`,
    label: `All of ${firstName}, drop ${secondName}`,
    spans: [{ setId: first.setId, startTs: first.set.startTs, endTs: first.set.endTs }],
    drops: [second.setId],
    minutesKept: [keep(first.setId, first.set.startTs, first.set.endTs)],
  });

  options.push({
    id: `${clash.id}:drop-first`,
    label: `All of ${secondName}, drop ${firstName}`,
    spans: [{ setId: second.setId, startTs: second.set.startTs, endTs: second.set.endTs }],
    drops: [first.setId],
    minutesKept: [keep(second.setId, second.set.startTs, second.set.endTs)],
  });

  return options;
}

/** Minutes of music the picks add up to, after arrive and leave choices. */
export function minutesOfMusic(resolved: ResolvedPick[]): number {
  return Math.round(resolved.reduce((a, p) => a + (p.endTs - p.startTs), 0) / MINUTE);
}

/** Walking minutes the picked order implies, counting consecutive pairs only. */
export function walkingMinutes(resolved: ResolvedPick[], walk: WalkMatrix): number {
  let total = 0;
  for (let i = 1; i < resolved.length; i++) {
    const from = resolved[i - 1].set.stageId;
    const to = resolved[i].set.stageId;
    if (from !== to) total += walkMinutes(walk, from, to);
  }
  return total;
}
